import { useState, useMemo, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import Select, { components } from "react-select";
import axios from 'axios';
import Description from "./description";
import Spinner from "react-bootstrap/Spinner";

// tts.js – with live width detection for the textarea container (no shadcn/ui dependency)

export default function TTSPage() {
  const LIMIT = 2000;
  const [text, setText] = useState("");
  const [containerWidth, setContainerWidth] = useState(0);
  

  const count = text.length;
  const remaining = useMemo(() => LIMIT - count, [count]);
  
  
  // Languages options
  const options = [
    { value: "a", label: "American English" },
	{ value: "b", label: "British English" },
	{ value: "z", label: "中文 - Simplified Chinese" },
    { value: "h", label: "Hindi (India)" },
    { value: "e", label: "Español - Spanish" },
    { value: "f", label: "Français - French" },
    { value: "i", label: "Italiano - Italian" },
    { value: "j", label: "日本 - Japanese" },
    { value: "p", label: "Portugues - Brazilian Portuguese" }	
  ];
  
  const voicesByLanguage = {
    "a": [
      { value: "af_heart", label: "Jenny (Female)" },
      { value: "am_michael", label: "Michael (Male)" },
    ],
	"b": [
      { value: "bf_alice", label: "Alice (Female)" },
      { value: "bm_daniel", label: "Daniel (Male)" },
    ],
	"z": [
      { value: "zf_xiaoyi", label: "XIAO YI (Female)" },
      { value: "zm_yunxi", label: "YUN XI (Male)" },
    ],
    "h": [
      { value: "hf_beta", label: "Asha (Female)" },
      { value: "hm_omega", label: "Raj (Male)" },
    ],
	"e": [
      { value: "ef_dora", label: "Dora (Female)" },
      { value: "em_alex", label: "Alex (Male)" },
    ],
	"f": [
      { value: "ff_siwis", label: "Diri (Female)" },
    ],
	"i": [
      { value: "if_sara", label: "Sara (Female)" },
      { value: "im_nicola", label: "Nicola (Male)" },
    ],
	"j": [
      { value: "jf_nezumi", label: "Nezumi (Female)" },
      { value: "jm_kumo", label: "Kumo (Male)" },
    ],
	"p": [
      { value: "pf_dora", label: "Dora (Female)" },
      { value: "pm_alex", label: "Alex (Male)" },
    ],
  };  
  
  const [language, setLanguage] = useState(options[0]); // default: English (US)
  
  // Filter voices based on selected language
  //const voiceOptions = language ? voicesByLanguage[language.value] || [] : [];
  
  const voiceOptions = useMemo(
    () => (language ? voicesByLanguage[language.value] || [] : []),
    [language]
  );
  
  const [voice, setVoice] = useState(voiceOptions[0] || null);
  
  useEffect(() => {
    // whenever language changes, select first available voice
    setVoice(voiceOptions[0] || null);
  }, [language, voiceOptions]);
  
  
  // Ref to the container that constrains the textarea width
  const containerRef = useRef(null);

  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  
  const [jobId, setJobId] = useState(null);
  const [polling, setPolling] = useState(false);

  const handleUpload = async () => {
    if (!text) return alert("Please enter the text first.");
    setLoading(true);
    setResult("");
    setJobId(null);

    const formData = new FormData();
    formData.append("text", text);
    formData.append("language", language.value);
	formData.append("voice", voice.value);
    formData.append("user_agent", navigator.userAgent);

    try {
      const res = await axios.post("https://ttsapi.pscribes.com/jobs", formData);
	  
	  if (res.data && res.data.job_id) {
		console.log(`Job Id: ${res.data.job_id} `);
        setJobId(res.data.job_id);
        setPolling(true);
        pollTranscriptionStatus(res.data.job_id);
      } else {
        alert(res.data.error || "Unexpected response from server.");
      }
    } catch (err) {
      if (err.response?.status === 429) {
        alert(err.response.data.error);
      } else {
        alert("Something went wrong during request. " + err.response);    //err.message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const pollTranscriptionStatus = async (jobId) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`https://ttsapi.pscribes.com/ttsapi?jobId=${jobId}`);
        if (res.data.status === "success") {
          clearInterval(interval);
          setResult(res.data.url);
		  setPolling(false);
        } else if (res.data.status === "failed") {
          clearInterval(interval);
          setPolling(false);
          alert("Text-To-Speech request failed: " + res.data.error);
        }
      } catch (err) {
        clearInterval(interval);
        setPolling(false);
        alert("Polling error: " + err.message);
      }
    }, 7000);
  };


  // Measure width on mount & whenever the element resizes
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => setContainerWidth(el.offsetWidth);
    update(); // initial

    // Prefer ResizeObserver for accurate element-based resize
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      // Fallback: listen to window resize
      window.addEventListener("resize", update);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", update);
    };
  }, []);

  const handleClear = () => setText("");
  
  // Custom option component
  const CustomOption = (props) => (
    <components.Option {...props}>
      <div className="flex justify-between items-center">
        <span>{props.label}</span>
        {props.isSelected && <span>✓</span>}
      </div>
    </components.Option>
  );
  
  const Spinner = () => (
    <div className="flex justify-center items-center mt-4">
      <div className="w-8 h-8 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
    </div>
  );
  
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "white" : state.isFocused ? "#f3f4f6" : "white",
      color: "black",
	  borderRadius: "0.5rem", // round edges on each option
      padding: "10px 15px",
    }),
	menu: (provided) => ({
      ...provided,
      borderRadius: "0.75rem", // round dropdown itself
      overflow: "hidden",
    }),
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto max-w-5xl px-4 py-8"
    >
	  <div className="mb-10">
	    <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-600 leading-tight mb-3 text-center">
          Text to Speech AI Converter Online
        </h1>
	  </div>
      {/* Top actions */}
      <div className="mb-3 flex items-center justify-end gap-4 text-sm">
	  
        <div className="tabular-nums text-gray-600">
          {count} / {LIMIT}
        </div>
        
        <button
          className="rounded-2xl bg-gray-100 px-4 py-2 text-sm shadow-sm hover:bg-gray-200 disabled:opacity-50"
          onClick={handleClear}
          disabled={!text}
          aria-label="Clear Text"
        >
          Clear Text
        </button>
		
      </div>

      {/* Width-constrained container with ref */}
      <div ref={containerRef} className="w-full">
	  
        {/* Text area card */}
        <div className="rounded-3xl border border-gray-200 bg-white shadow-sm">
          
          <textarea
            id="tts-input"
            value={text}
            onChange={(e) => {
              const v = e.target.value.slice(0, LIMIT);
              setText(v);
            }}
            placeholder="Enter your text here..."
            maxLength={LIMIT}
			className="w-full h-64 p-4 border-2 border-gray-300 rounded-2xl text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
            />
        </div>
  
      </div>


      {/* Bottom helper row (optional) */}
	  
      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <span>
          Tip: You can paste up to {LIMIT.toLocaleString()} characters.
        </span>
        <span className={remaining < 0 ? "text-red-600" : undefined}>
          {remaining} characters left
        </span>
      </div>
	  
	  <div className="mt-3 col-12 col-lg-5">
        <div className="mb-3 d-md-flex justify-content-md-between align-items-md-center text-start">
          <label className="form-label">Language</label>
        </div>
		
		<div className="mb-3 w-1/2">
		  <Select
            options={options}
			value={language}
            onChange={(selected) => setLanguage(selected)}
			isSearchable
			components={{ Option: CustomOption }}
			styles={customStyles} />
		</div>
		
		<div className="mt-3 d-md-flex justify-content-md-between align-items-md-center text-start">
            <label className="form-label">Voice</label>
        </div>
		
		<div className="mt-3 mb-3 w-1/2">
		  <Select
            options={voiceOptions}
		    value={voice}
          onChange={setVoice}
          components={{ Option: CustomOption }}
          styles={customStyles}
          isDisabled={!language} // disable until a language is picked
        />
		</div>
		
		{!polling && (
		  <div className="flex justify-center">
            <button
			  onClick={handleUpload}
			  disabled={loading}
              className="mt-6 mb-6 w-80 rounded-2xl bg-blue-200 px-4 py-2 text-sm font-bold shadow-sm hover:bg-blue-300"
            >
              {loading ? "Uploading..." : "Generate Speech"}
            </button>
		  </div>
		)}
		
		{polling && (
          <div className="flex flex-col items-center mt-4 space-y-2">
		    <Spinner />
              <p className="text-blue-600 font-medium">In progress... Please wait.</p>
          </div>
        )}
		
        {result && (
		  <div className="mb-6">
		    <audio className=" col-12 mt-2 mb-10" controls="controls" autoPlay src={result} />
		    <a
              href={result}
              download="TTSAudio.wav"
              className="mt-6 inline-block rounded-full bg-blue-500 px-6 py-2 text-white font-bold hover:bg-blue-700"
              rel="noopener"
            >
            Download Audio File
            </a>
		  </div>
		)}
		
		<div className="mt-6">
          <Description />
        </div>
		
		
      </div>
    </motion.div>
  );
}
